<template>
    <ion-page cy-data="home-page-header">
        <ion-content>
            <span v-if="isMobile" cy-data="mobile-rewards-header">
                <MiniGuestRewardsBanner v-if="!isLoggedIn" />
                <MiniRewardsBanner v-if="isLoggedIn" />
            </span>

            <HomePagePromo :promo="mainPromo" id="first-promo" />

            <span v-if="isMobile">
                <ScrollOrderHistory v-if="isLoggedIn && hasOrderHistory" />
            </span>

            <span v-if="!isMobile">
                <GuestRewardsBanner v-if="!isLoggedIn" />
                <RewardsBanner v-if="isLoggedIn" />
            </span>

            <HomePagePromo v-for="(promo, idx) in promos.slice(1)" :key="idx" :promo="promo" />
            <Footer />
        </ion-content>

        <StartYourOrderBanner v-if="!isNative" />

        <modalWrapper
            maxHeight="433px"
            height="450px"
            maxWidth="310px"
            minHeight="439px"
            :canDismiss="true"
            :open="state.modalOpen"
            :hideToolbar="true"
            @close="state.modalOpen = false"
            @dismiss="state.modalOpen = false"
            class="d-none-web"
        >
            <ion-row class="full-width border-red">
                <ion-col class="full-width text-center px-m8">
                    <img src="@/assets/pep/HappyFaceWinkingThumbsUp.png" alt="" style="height: 170px; width: 170px" />
                    <h1 class="margin-top-0">Please update your app to continue...</h1>
                    <ion-button class="mobile-update" @click="openAppStore">OK</ion-button>
                    <p class="font-size-14">
                        The latest version of our app features <br />
                        improvements no app user can live without.
                    </p>
                    <p class="font-size-14">Can't update now? Switch to <a href="https://www.donatos.com" style="color: #ad1b29">donatos.com</a></p>
                </ion-col>
            </ion-row>
        </modalWrapper>
    </ion-page>
</template>

<script lang="ts">
import { onIonViewWillEnter } from '@ionic/vue';
import { defineComponent, computed, onBeforeMount, reactive, onMounted } from 'vue';
import { useStore } from 'vuex';
import LocationManager from '@/utils/locationManager';
import { useUrlQueryParser } from '@/composables/useUrlQueryParser';
import { Promo } from '@/models/promo';

// Components
import { IonContent, IonPage, IonRow, IonCol, IonButton } from '@ionic/vue';
import MiniGuestRewardsBanner from '@/components/account/miniGuestRewardsBanner.vue';
import MiniRewardsBanner from '@/components/account/miniRewardsBanner.vue';

import GuestRewardsBanner from '@/components/account/guestRewardsBanner.vue';
import HomePagePromo from '@/components/homePagePromo.vue';
import RewardsBanner from '@/components/account/rewardsBanner.vue';
import ScrollOrderHistory from '@/components/scrollOrderHistory.vue';
import Footer from '@/components/layout/Footer.vue';
import StartYourOrderBanner from '@/components/layout/startYourOrderBanner.vue';
import modalWrapper from '@/components/modals/modalWrapper.vue';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import vaultService from '@/services/vaultService';

export default defineComponent({
    setup() {
        const { clearVault } = vaultService();

        const state = reactive({
            modalOpen: false,
        });

        const store = useStore();
        const { handleQueries } = useUrlQueryParser();
        const selectedLocationId = computed(() => store.getters.getSelectedLocation?.id ?? LocationManager.getInstance().getLocationId());
        const isNative = computed(() => store.getters.isNative);
        const isMobile = computed(() => store.getters.isMobile);
        const isLoggedIn = computed(() => store.getters.isLoggedIn);
        const hasOrderHistory = computed(() => store.getters.getHasOrderHistory);

        const allPromos = computed(() => store.getters.getHomepageBlockList.map((promo) => new Promo(promo)).sort((a, b) => (a.weight > b.weight ? 1 : -1)));

        const defaultPromos = computed(() => allPromos.value.filter((promo: Promo) => promo.locationId.includes(LocationManager.getInstance().getLocationId())));

        const locationSpecificPromos = computed(() => allPromos.value.filter((promo: Promo) => promo.locationId.includes(selectedLocationId.value) || promo.locationId.includes('-1')));

        const promos = computed(() => (locationSpecificPromos.value.length > 0 ? locationSpecificPromos.value : defaultPromos.value));
        const mainPromo = computed(() => promos.value[0]);

        // App Update dialog
        const performImmediateUpdate = async () => {
            try {
                const result = await AppUpdate.getAppUpdateInfo();
                if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) return;
                if (result.immediateUpdateAllowed) {
                    // Clear app storage
                    localStorage.clear();
                    await clearVault();

                    // Perform immediate update

                    await AppUpdate.performImmediateUpdate();
                }
            } catch (error) {
                console.error('perform immediate update error ', error);
            }
        };

        const openAppStore = async () => {
            state.modalOpen = false;
            await AppUpdate.openAppStore();
        };

        onMounted(() => {
            if (isNative.value) {
                // Trigger update check
                performImmediateUpdate();
            }
        });

        onBeforeMount(() => {
            store.dispatch('setHomepageBlockList');
        });

        onIonViewWillEnter(() => {
            handleQueries();
        });

        return {
            state,
            promos,
            isNative,
            isMobile,
            isLoggedIn,
            mainPromo,
            hasOrderHistory,
            openAppStore,
        };
    },
    props: {},
    components: {
        IonButton,
        IonCol,
        IonPage,
        IonRow,
        IonContent,
        Footer,
        HomePagePromo,
        RewardsBanner,
        ScrollOrderHistory,
        MiniGuestRewardsBanner,
        MiniRewardsBanner,
        GuestRewardsBanner,
        StartYourOrderBanner,
        modalWrapper,
    },
});
</script>

<style scoped>
#first-promo {
    max-height: 65vh;
}
.border-red {
    border: 3px solid #ab2328;
}
@media only screen and (min-width: 900px) {
    .d-none-web {
        display: none;
    }

    p {
        margin-block-start: 0;
        margin-block-end: 0;
    }
}
</style>
